import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  FormControl, 
  FormControlLabel, 
  FormLabel, 
  Radio, 
  RadioGroup, 
  Checkbox, 
  Alert, 
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  InputAdornment
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { residentContactService } from '../services/api';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Hero from '../components/common/Hero';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HouseIcon from '@mui/icons-material/House';
import NotesIcon from '@mui/icons-material/Notes';

const UpdateContact = () => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    secondaryEmail: '',
    preferredContactMethod: 'email',
    residentType: 'owner',
    receiveNotifications: true,
    notes: ''
  });
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await residentContactService.createOrUpdate(formData);
      
      if (response.success) {
        setSuccess(true);
        // No longer automatically redirecting
      } else {
        setError(response.error || 'Failed to update contact information');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = () => {
    setSuccess(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      secondaryEmail: '',
      preferredContactMethod: 'email',
      residentType: 'owner',
      receiveNotifications: true,
      notes: ''
    });
  };
  
  const handleCloseSnackbar = () => {
    setError(null);
  };
  
  // If submission was successful, show success view
  if (success) {
    return (
      <>
        <Helmet>
          <title>Contact Information Updated | Brynleigh Estates</title>
          <meta name="description" content="Successfully updated your contact information for Brynleigh Estates HOA" />
        </Helmet>
        
        <Hero 
          imageUrl="/images/woods-bg.jpeg"
          title="Contact Information Updated"
          subtitle="Thank you for keeping your information current"
        />
        
        <Container maxWidth="md" sx={{ my: 6 }}>
          <Card elevation={2} sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
            <CardContent>
              <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              
              <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                Thank You!
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 3 }}>
                Your contact information has been successfully updated.
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 4 }}>
                We appreciate you keeping your information up to date. This helps us communicate effectively with all residents about important community matters.
              </Typography>
            </CardContent>
            
            <CardActions sx={{ justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to="/"
                startIcon={<HomeIcon />}
              >
                Return to Home
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
              >
                Submit Another Entry
              </Button>
            </CardActions>
          </Card>
        </Container>
      </>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Update Contact Information | Brynleigh Estates HOA</title>
        <meta name="description" content="Update your contact information for Brynleigh Estates HOA. Keep your details current to stay connected with our community." />
        <link rel="canonical" href="https://brynleighestates.com/update-contact" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/update-contact" />
        <meta property="og:title" content="Update Contact Information | Brynleigh Estates HOA" />
        <meta property="og:description" content="Update your contact information for Brynleigh Estates HOA. Keep your details current to stay connected with our community." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Update Contact Information | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Update your contact information for Brynleigh Estates HOA." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/woods-bg.jpeg"
        title="Update Contact Information"
        subtitle="Keep your information up to date to stay connected with our community"
      />
      
      <Container maxWidth="md" sx={{ my: 6 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
            Resident Contact Information
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  helperText="Primary email for HOA communications"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Secondary Email Address"
                  name="secondaryEmail"
                  type="email"
                  value={formData.secondaryEmail}
                  onChange={handleInputChange}
                  variant="outlined"
                  helperText="Optional secondary email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Street Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HouseIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Preferred Contact Method</FormLabel>
                  <RadioGroup
                    name="preferredContactMethod"
                    value={formData.preferredContactMethod}
                    onChange={handleInputChange}
                    row
                  >
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                    <FormControlLabel value="mail" control={<Radio />} label="Mail" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Resident Type</FormLabel>
                  <RadioGroup
                    name="residentType"
                    value={formData.residentType}
                    onChange={handleInputChange}
                    row
                  >
                    <FormControlLabel value="owner" control={<Radio />} label="Property Owner" />
                    <FormControlLabel value="renter" control={<Radio />} label="Property Renter" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="receiveNotifications"
                      checked={formData.receiveNotifications}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Receive community notifications"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  variant="outlined"
                  multiline
                  rows={4}
                  helperText="Any additional information you'd like to share"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <NotesIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Contact Information'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateContact; 